import { authStore } from './auth.js';
import { clearUserRoles } from './roles.js';
import { courtesyXmlState } from '../lib/courtesy-pdf.js';

const API_BASE = import.meta.env.VITE_API_URL || '';

/**
 * Read the human message out of an error body.
 *
 * The API answers errors in three shapes: `{ error }` from our own handlers,
 * `{ error, message }` from the role guard, and a bare `{ message }` from
 * SuperTokens when a session has expired. A body that is not JSON at all (an
 * HTML error page from a proxy, say) yields no message — dumping markup into
 * the error banner reads as a crash, so the caller falls back to the status.
 */
function readErrorBody(rawText) {
  let body = null;
  try {
    body = JSON.parse(rawText);
  } catch {
    // Not JSON — nothing here is fit to show a user.
  }
  if (!body || typeof body !== 'object') {
    return {
      message: '', code: null, details: null, matches: null, windowHours: null, duplicateCheckToken: null,
      residuo: null, pagamento: null, field: null, existingId: null, fileStatus: null, detail: null,
    };
  }
  const message = [body.error, body.message]
    .find((v) => typeof v === 'string' && v.trim().length > 0);
  return {
    message: message || '',
    code: typeof body.code === 'string' ? body.code : null,
    details: Array.isArray(body.details) ? body.details : null,
    // The documents a `possible_duplicate` refusal names, for the form to list.
    matches: Array.isArray(body.matches) ? body.matches : null,
    windowHours: typeof body.windowHours === 'number' ? body.windowHours : null,
    // What «Emetti comunque» sends back: it acknowledges that warning only.
    duplicateCheckToken: typeof body.duplicateCheckToken === 'string' ? body.duplicateCheckToken : null,
    // A payment refused because the residual moved carries the residual it
    // found and the summary it belongs to, for the dialog to propose again.
    residuo: typeof body.residuo === 'number' ? body.residuo : null,
    pagamento: body.pagamento && typeof body.pagamento === 'object' ? body.pagamento : null,
    // The anagrafica field an address or contacts refusal is about, so the
    // form can point at it.
    field: typeof body.field === 'string' ? body.field : null,
    // A bilancio refusal: the period's existing bilancio (`bilancio_exists`),
    // the state of an archived copy that failed its check
    // (`bilancio_file_integrity`), and what the file was refused for.
    existingId: typeof body.existingId === 'string' ? body.existingId : null,
    fileStatus: typeof body.fileStatus === 'string' ? body.fileStatus : null,
    detail: body.detail && typeof body.detail === 'object' && !Array.isArray(body.detail) ? body.detail : null,
  };
}

/**
 * An expired session must return the user to the login screen, not leave an
 * error banner on every screen until they think to reload. supertokens-web-js
 * already tries a silent refresh before any 401 reaches us, so by this point
 * the session is genuinely gone: clearing the auth store makes the shell
 * render <Login /> on the spot (see App.svelte).
 */
function handleUnauthorized() {
  clearUserRoles();
  authStore.set({ isAuthenticated: false, user: null, loading: false, serverDown: false });
}

/**
 * Scarica un file dall'API. Non passa da apiCall() perché la risposta è il file
 * stesso — XML o .p7m — e non JSON, ma solleva lo stesso tipo di errore, così
 * il banner classifica un download fallito come qualsiasi altra chiamata.
 *
 * @param {string} url endpoint assoluto
 * @param {string} fallbackFilename usato se la risposta non porta Content-Disposition
 * @returns {Promise<{blob: Blob, filename: string, headers: Headers}>}
 */
async function downloadAttachment(url, fallbackFilename, { method = 'GET', body, headers = {} } = {}) {
  const options = { credentials: 'include' };
  if (method !== 'GET') options.method = method;
  if (body !== undefined) {
    options.headers = { 'Content-Type': 'application/json', ...headers };
    options.body = JSON.stringify(body);
  } else if (Object.keys(headers).length) {
    options.headers = headers;
  }
  const res = await fetch(url, options);
  if (!res.ok) {
    const { message, code, fileStatus } = readErrorBody(await res.text());
    if (res.status === 401) handleUnauthorized();
    const err = new Error(message || `HTTP ${res.status}`);
    err.status = res.status;
    err.serverMessage = Boolean(message);
    if (code) err.code = code;
    if (fileStatus) err.fileStatus = fileStatus;
    throw err;
  }
  const disposition = res.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="([^"]+)"/);
  return { blob: await res.blob(), filename: match?.[1] || fallbackFilename, headers: res.headers };
}

async function apiCall(endpoint, options = {}) {
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;
  const config = {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };
  if (options.body && typeof options.body === 'object') {
    config.body = JSON.stringify(options.body);
  }

  const response = await fetch(url, config);
  const rawText = await response.text();

  if (!response.ok) {
    const {
      message, code, details, matches, windowHours, duplicateCheckToken, residuo, pagamento, field,
      existingId, fileStatus, detail,
    } = readErrorBody(rawText);
    if (response.status === 401) handleUnauthorized();

    const err = new Error(message || `HTTP ${response.status}`);
    // The status is what friendlyError() classifies on: the backend messages
    // are already Italian prose, so guessing their meaning from keywords is
    // both unreliable and unnecessary.
    err.status = response.status;
    // True when the backend supplied a message written for a human — that one
    // is more useful than any generic sentence we could substitute for it.
    err.serverMessage = Boolean(message);
    // Carry the machine-readable parts through: a FatturaPA validation failure
    // answers with the full list of what is wrong with the invoice, and that
    // list is the whole point of the response.
    if (code) err.code = code;
    if (details) err.details = details;
    if (matches) err.matches = matches;
    if (windowHours !== null) err.windowHours = windowHours;
    if (duplicateCheckToken) err.duplicateCheckToken = duplicateCheckToken;
    if (residuo !== null) err.residuo = residuo;
    if (pagamento) err.pagamento = pagamento;
    if (field) err.field = field;
    if (existingId) err.existingId = existingId;
    if (fileStatus) err.fileStatus = fileStatus;
    if (detail) err.detail = detail;
    throw err;
  }

  if (!rawText) return {};
  try {
    return JSON.parse(rawText);
  } catch {
    return {};
  }
}

// ── Invoices ──────────────────────────────────────────
//
// NOTE: everything under /api/admin is cross-tenant and gated on the global
// `admin` role. The UI is always looking at ONE company — the one in the
// sidebar switcher — so it must go through `companyInvoicesApi` below, which
// scopes on :companyId and is open to every member of that company. Reaching
// for an /api/admin twin here answers 403 for operators and readonly users,
// and shows an admin other companies' invoices.

export async function fetchInvoice(id) {
  return apiCall(`/api/admin/invoices/${id}`);
}

export async function fetchInvoiceTransitions(id) {
  return apiCall(`/api/admin/invoices/${id}/transitions`);
}

export async function fetchInvoiceHistory(id) {
  return apiCall(`/api/admin/invoices/${id}/history`);
}

export async function fetchInvoiceTransmissions(id) {
  return apiCall(`/api/admin/invoices/${id}/transmissions`);
}

export async function fetchInvoiceNotifications(id) {
  return apiCall(`/api/admin/invoices/${id}/notifications`);
}

export async function createInvoice(data) {
  return apiCall('/api/admin/invoices', {
    method: 'POST',
    body: data,
  });
}

// ── Notifications ─────────────────────────────────────

export async function fetchNotificationsBySdiId(identificativoSdi) {
  return apiCall(`/api/admin/notifications/sdi/${identificativoSdi}`);
}

// ── Transport Logs ────────────────────────────────────

export async function fetchTransportLogs(correlationId) {
  return apiCall(`/api/admin/transport-logs/${correlationId}`);
}

// ── Diagnostics ───────────────────────────────────────

export async function fetchDiagConfig() {
  return apiCall('/api/admin/diagnostics/config');
}

export async function fetchDiagCerts() {
  return apiCall('/api/admin/diagnostics/certs');
}

/**
 * Whether the background sender is paused, and on what.
 *
 * The queue can be held for hours while SdI or the signature provider is down.
 * Without this the screen shows a pile of invoices in "in coda" and no reason.
 */
export async function fetchSendQueueState() {
  return apiCall('/api/admin/diagnostics/send-queue');
}

// ── Received invoices with no owning company ──────────

/**
 * Invoices SdI delivered that matched no company, and the repair for them.
 *
 * Such an invoice is stored but filtered out of every company-scoped view, so
 * this is the only place it can be seen at all.
 */
export const unattributedInvoicesApi = {
  async list() { return apiCall('/api/admin/invoices/unattributed'); },
  async reattribute() {
    return apiCall('/api/admin/invoices/unattributed/reattribute', { method: 'POST' });
  },
};

// ── Health ────────────────────────────────────────────

export async function fetchHealth() {
  const response = await fetch('/health');
  return response.json();
}

// ── Companies ─────────────────────────────────────────

export const companiesApi = {
  async list() { return apiCall('/api/companies'); },
  async get(id) { return apiCall(`/api/companies/${id}`); },
  async create(data) { return apiCall('/api/companies', { method: 'POST', body: data }); },
  async update(id, data) { return apiCall(`/api/companies/${id}`, { method: 'PUT', body: data }); },
  async remove(id) { return apiCall(`/api/companies/${id}`, { method: 'DELETE' }); },
  async listUsers(id) { return apiCall(`/api/companies/${id}/users`); },
  async inviteUser(id, data) { return apiCall(`/api/companies/${id}/users`, { method: 'POST', body: data }); },
  async updateUserRole(companyId, userId, role) { return apiCall(`/api/companies/${companyId}/users/${userId}`, { method: 'PUT', body: { role } }); },
  async removeUser(companyId, userId) { return apiCall(`/api/companies/${companyId}/users/${userId}`, { method: 'DELETE' }); },
  async switchTo(id) { return apiCall(`/api/companies/${id}/switch`, { method: 'POST' }); },
  /** Cosa può fare l'utente in questa azienda (ruolo nell'azienda, email). */
  async capabilities(id) { return apiCall(`/api/companies/${id}/capabilities`); },
};

// ── Impostazioni email (SMTP) ─────────────────────────
//
// Solo per gli amministratori dell'azienda. La password non torna mai dal
// server: `passwordSet` dice soltanto se ce n'è una salvata.

export const smtpApi = {
  async get(companyId) { return apiCall(`/api/companies/${companyId}/smtp`); },
  async save(companyId, settings) {
    return apiCall(`/api/companies/${companyId}/smtp`, { method: 'PUT', body: settings });
  },
  async remove(companyId) { return apiCall(`/api/companies/${companyId}/smtp`, { method: 'DELETE' }); },
  async test(companyId, recipient) {
    return apiCall(`/api/companies/${companyId}/smtp/test`, {
      method: 'POST', body: recipient ? { recipient } : {},
    });
  },
};

// ── User preferences ──────────────────────────────────
//
// Session-authenticated and scoped to the caller: there is no user id in the
// path because the server takes it from the session and never from the
// request.

export const userPreferencesApi = {
  async get() { return apiCall('/api/user-preferences/me'); },
  async patch(preferences) {
    return apiCall('/api/user-preferences/me', { method: 'PATCH', body: preferences });
  },
};

// ── Customers ─────────────────────────────────────────

export const customersApi = {
  /**
   * The company's anagrafica.
   *
   * @param {string} companyId
   * @param {object} [opts]
   * @param {'cliente'|'fornitore'|'entrambi'} [opts.kind] commercial role.
   * @param {'pa'|'privato'|'scissione'} [opts.destinazione] who the invoices go
   *   to — `pa` is a six-character Codice Univoco Ufficio, `privato` everything
   *   else, and `scissione` the counterparts whose VAT the customer may have to
   *   pay: every `pa` record, plus the ones this company has already invoiced
   *   with EsigibilitaIVA `S`. That second half is the only way to find a
   *   foundation or a state-controlled company under art. 17-ter, since nothing
   *   in the document distinguishes it from any other private counterpart.
   * @param {'mancante'|'I'|'D'|'S'} [opts.esigibilitaIva] recorded default;
   *   `mancante` returns the records carrying no choice at all. Combined with
   *   `destinazione=scissione` this is the set that still owes a choice.
   */
  async list(companyId, { kind, destinazione, esigibilitaIva } = {}) {
    const params = new URLSearchParams();
    if (kind) params.set('kind', kind);
    if (destinazione) params.set('destinazione', destinazione);
    if (esigibilitaIva) params.set('esigibilitaIva', esigibilitaIva);
    const qs = params.toString();
    return apiCall(`/api/companies/${companyId}/customers${qs ? `?${qs}` : ''}`);
  },
  async get(companyId, id) { return apiCall(`/api/companies/${companyId}/customers/${id}`); },
  async create(companyId, data) { return apiCall(`/api/companies/${companyId}/customers`, { method: 'POST', body: data }); },
  async update(companyId, id, data) { return apiCall(`/api/companies/${companyId}/customers/${id}`, { method: 'PUT', body: data }); },
  async remove(companyId, id) { return apiCall(`/api/companies/${companyId}/customers/${id}`, { method: 'DELETE' }); },
  /**
   * Autocomplete over the anagrafica.
   * @param {{kind?: 'cliente'|'fornitore'|'entrambi'}} [opts] restrict the
   *   search to one role — the supplier picker on an autofattura must not
   *   offer clienti. 'cliente'/'fornitore' also match dual-role records.
   */
  async search(companyId, q, { kind } = {}) {
    const params = new URLSearchParams({ q });
    if (kind) params.set('kind', kind);
    return apiCall(`/api/companies/${companyId}/customers/search?${params}`);
  },
};

// ── Commercialista ────────────────────────────────────

export const commercialistaApi = {
  async listCompanies() { return apiCall('/api/commercialista/companies'); },
  async listInvoices({ companyId, status, direction, limit } = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    if (status) params.set('status', status);
    if (direction) params.set('direction', direction);
    if (limit) params.set('limit', String(limit));
    return apiCall(`/api/commercialista/invoices?${params}`);
  },
  /**
   * Esportazione contabile di un periodo. Senza `from`/`to` restituisce
   * l'intero archivio, che oltre il limite del server arriva troncato: il
   * campo `truncated` della risposta dice se e successo.
   */
  async exportInvoices(companyId, { from, to, direction } = {}) {
    return apiCall(`/api/commercialista/companies/${companyId}/invoices/export?${
      exportParams({ from, to, direction })}`);
  },

  /**
   * Lo stesso periodo come archivio ZIP: riepilogo, XML delle fatture e
   * ricevute SdI. La ricevuta e cio che prova che una fattura e stata emessa,
   * quindi e la parte che il commercialista non puo ricostruire da solo.
   * @returns {Promise<{blob: Blob, filename: string}>}
   */
  async downloadExportZip(companyId, { from, to, direction } = {}) {
    const span = [from, to].filter(Boolean).join('_') || 'tutto';
    return downloadAttachment(
      `${API_BASE}/api/commercialista/companies/${companyId}/invoices/export?${
        exportParams({ from, to, direction, format: 'zip' })}`,
      `fatture_${span}.zip`,
    );
  },
};

function exportParams({ from, to, direction, format } = {}) {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (direction) params.set('direction', direction);
  if (format) params.set('format', format);
  return params.toString();
}

// ── Company Invoices ──────────────────────────────────

export const companyInvoicesApi = {
  async list(companyId, { status, direction, limit, formatoTrasmissione, year, from, to, pagamento, letta } = {}) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (direction) params.set('direction', direction);
    if (formatoTrasmissione) params.set('formatoTrasmissione', formatoTrasmissione);
    if (year) params.set('year', String(year));
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (pagamento) params.set('pagamento', pagamento);
    if (letta) params.set('letta', letta);
    if (limit) params.set('limit', String(limit));
    return apiCall(`/api/companies/${companyId}/invoices?${params}`);
  },
  async downloadSelectedXml(companyId, ids) {
    return downloadAttachment(
      `${API_BASE}/api/companies/${companyId}/invoices/export/xml`,
      `fatture_xml_${companyId}.zip`,
      { method: 'POST', body: { ids } },
    );
  },
  /**
   * Quarterly IVA totals (imponibile + IVA) for this company's outbound
   * invoices, the figures that go into the periodic IVA return.
   */
  async quarterlyReport(companyId, { year, formatoTrasmissione } = {}) {
    const params = new URLSearchParams();
    if (year) params.set('year', String(year));
    if (formatoTrasmissione) params.set('formatoTrasmissione', formatoTrasmissione);
    return apiCall(`/api/companies/${companyId}/invoices/reports/quarterly?${params}`);
  },
  async get(companyId, id) { return apiCall(`/api/companies/${companyId}/invoices/${id}`); },
  /**
   * Registra un pagamento su una fattura: l'incasso di una emessa, il pagamento
   * di una ricevuta. Aggiorna solo le rate; la fattura e il suo stato SdI non
   * cambiano. `residuoAtteso` è il residuo letto sulla riga: se nel frattempo è
   * cambiato il server rifiuta (409), così lo stesso pagamento non passa due volte.
   */
  async recordPayment(companyId, id, body) {
    return apiCall(`/api/companies/${companyId}/invoices/${id}/payments`, { method: 'POST', body });
  },
  /**
   * Segna fatture ricevute come lette o da leggere: `{ ids, isRead }` (al massimo
   * 500 id) oppure `{ all: true, isRead: true }` per segnarle tutte come lette.
   * Risponde con quante ne ha cambiate e quante restano da leggere.
   */
  async setReadState(companyId, body) {
    return apiCall(`/api/companies/${companyId}/invoices/read-state`, { method: 'POST', body });
  },
  async create(companyId, data) { return apiCall(`/api/companies/${companyId}/invoices`, { method: 'POST', body: data }); },
  async getTransitions(companyId, id) { return apiCall(`/api/companies/${companyId}/invoices/${id}/transitions`); },
  async getHistory(companyId, id) { return apiCall(`/api/companies/${companyId}/invoices/${id}/history`); },
  async getTransmissions(companyId, id) { return apiCall(`/api/companies/${companyId}/invoices/${id}/transmissions`); },
  async getNotifications(companyId, id) { return apiCall(`/api/companies/${companyId}/invoices/${id}/notifications`); },
  async sendEsito(companyId, id, data) { return apiCall(`/api/companies/${companyId}/invoices/${id}/send-esito`, { method: 'POST', body: data }); },
  async retrySend(companyId, id) { return apiCall(`/api/companies/${companyId}/invoices/${id}/retry-send`, { method: 'POST' }); },
  /**
   * Corregge i dati di una fattura scartata, senza trasmettere niente.
   *
   * Lo scarto è l'unico rifiuto che lascia il documento correggibile: SdI non
   * l'ha mai accettato, quindi il numero non è consumato e la stessa fattura
   * può essere ritrasmessa. I campi vengono uniti a quelli già memorizzati —
   * si manda solo quello che cambia — e `lineItems` e `pagamento` sostituiscono
   * l'intero blocco. Il totale viene ricalcolato dal server quando cambiano le
   * righe o l'esigibilità.
   *
   * La correzione non viene rifiutata se il documento resta incompleto: quello
   * che manca torna in `warnings` e la fattura resta in `scarto` finché non la
   * si rimette in coda con reopen().
   */
  async correct(companyId, id, data) {
    return apiCall(`/api/companies/${companyId}/invoices/${id}`, { method: 'PATCH', body: data });
  },
  /**
   * Riapre una fattura scartata: torna in stato `created`, l'XML viene
   * rigenerato e il documento rimesso in coda. Ammesso solo da `scarto`.
   *
   * La rigenerazione butta via l'XML precedente e con esso la firma apposta
   * sopra: se l'azienda firma fuori dal gestionale serve un nuovo .p7m, e
   * `awaitingManualSignature` è l'unico campo che lo dice. Va letto con quel
   * nome — è quello che l'endpoint risponde (vedi src/api/openapi.js).
   */
  async reopen(companyId, id) {
    const res = await apiCall(`/api/companies/${companyId}/invoices/${id}/reopen`, { method: 'POST' });
    return {
      status: res?.status ?? null,
      manualSignatureRequired: Boolean(res?.awaitingManualSignature),
    };
  },
  async getUnreadCount(companyId) { return apiCall(`/api/companies/${companyId}/invoices/unread-count`); },

  // ── Firma apposta fuori dal gestionale (firma digitale personale) ──

  /** Stato della firma: serve una firma manuale? ne è già stata caricata una? */
  async getSignatureStatus(companyId, id) {
    return apiCall(`/api/companies/${companyId}/invoices/${id}/signature`);
  },

  /**
   * Scarica l'XML della fattura. Vale in entrambe le direzioni: per una fattura
   * emessa restituisce l'XML generato — gli stessi byte che la firma dovrà
   * racchiudere — per una ricevuta il file originale consegnato da SdI.
   * @returns {Promise<{blob: Blob, filename: string}>}
   */
  async downloadXml(companyId, id) {
    return downloadAttachment(
      `${API_BASE}/api/companies/${companyId}/invoices/${id}/xml`, `${id}.xml`,
    );
  },

  /**
   * Scarica il file firmato (.p7m): la busta CAdES che contiene insieme la
   * fattura e la firma digitale, cioè il file effettivamente trasmesso a SdI.
   * Risponde 409 quando la fattura non è firmata.
   * @returns {Promise<{blob: Blob, filename: string}>}
   */
  async downloadSignedP7m(companyId, id) {
    return downloadAttachment(
      `${API_BASE}/api/companies/${companyId}/invoices/${id}/signed`, `${id}.xml.p7m`,
    );
  },

  /** Importi e condizioni per la richiesta di anticipo fattura. */
  async getAnticipoFattura(companyId, id) {
    return apiCall(`/api/companies/${companyId}/invoices/${id}/anticipo-fattura`);
  },

  /** Scarica il PDF della richiesta usando il valore calcolato dal server. */
  async downloadAnticipoFatturaPdf(companyId, id, mode) {
    const params = new URLSearchParams({ mode });
    return downloadAttachment(
      `${API_BASE}/api/companies/${companyId}/invoices/${id}/anticipo-fattura/pdf?${params}`,
      `${id}-anticipo-${mode}.pdf`,
    );
  },

  /**
   * Scarica la copia di cortesia in PDF di una fattura emessa. Non è il
   * documento fiscale (quello resta l'XML): è una rappresentazione leggibile da
   * girare al cliente. `branded` ha l'intestazione dell'azienda, `ade` mostra
   * ogni campo come il visualizzatore dell'Agenzia delle Entrate. Risponde 409
   * finché la fattura non risulta emessa dal Sistema di Interscambio.
   * @param {'branded'|'ade'} layout impaginazione richiesta
   * @returns {Promise<{blob: Blob, filename: string}>}
   */
  /** Le copie di cortesia via email di una fattura, e se se ne può inviare una. */
  async getCourtesyEmails(companyId, id) {
    return apiCall(`/api/companies/${companyId}/invoices/${id}/courtesy-email`);
  },

  /** Mette in coda l'invio della copia di cortesia; `recipient` facoltativo. */
  async sendCourtesyEmail(companyId, id, recipient) {
    return apiCall(`/api/companies/${companyId}/invoices/${id}/courtesy-email`, {
      method: 'POST', body: recipient ? { recipient } : {},
    });
  },

  /**
   * La copia di cortesia, e `xml`: se contiene il file XML archiviato
   * (`embedded`), no perché non si è potuto (`unavailable`) o perché non è
   * stato chiesto (`omitted`); null da un server che non lo dice.
   */
  async downloadPdf(companyId, id, layout = 'branded') {
    const { blob, filename, headers } = await downloadAttachment(
      `${API_BASE}/api/companies/${companyId}/invoices/${id}/pdf?layout=${encodeURIComponent(layout)}`,
      layout === 'ade' ? `${id}-ade.pdf` : `${id}.pdf`,
    );
    return { blob, filename, xml: courtesyXmlState(headers.get('x-courtesy-xml')) };
  },

  /**
   * Carica il .p7m firmato. Viene verificato lato server prima di essere
   * salvato: deve essere CAdES e deve racchiudere esattamente l'XML generato.
   * @param {string} contentBase64 contenuto del file, senza prefisso data:
   */
  async attachSigned(companyId, id, contentBase64, { replace = false } = {}) {
    return apiCall(`/api/companies/${companyId}/invoices/${id}/signed`, {
      method: 'POST',
      body: { content: contentBase64, replace },
    });
  },

  // ── Bozze (fatture senza numero, in preparazione) ───────────────

  /** Elenco delle bozze dell'azienda. */
  async listDrafts(companyId) {
    return apiCall(`/api/companies/${companyId}/invoices/drafts`);
  },
  /** Crea una nuova bozza, senza numero né data di emissione. */
  async createDraft(companyId, body) {
    return apiCall(`/api/companies/${companyId}/invoices/drafts`, { method: 'POST', body });
  },
  async getDraft(companyId, draftId) {
    return apiCall(`/api/companies/${companyId}/invoices/drafts/${draftId}`);
  },
  /** Aggiorna una bozza: i campi passati sostituiscono, il resto resta invariato. */
  async updateDraft(companyId, draftId, body) {
    return apiCall(`/api/companies/${companyId}/invoices/drafts/${draftId}`, { method: 'PATCH', body });
  },
  /** Elimina la bozza: non è un documento fiscale, quindi la cancellazione è definitiva. */
  async deleteDraft(companyId, draftId) {
    return apiCall(`/api/companies/${companyId}/invoices/drafts/${draftId}`, { method: 'DELETE' });
  },
  /** Controlla la bozza senza emetterla. Risponde { valid, issues, warnings }. */
  async validateDraft(companyId, draftId) {
    return apiCall(`/api/companies/${companyId}/invoices/drafts/${draftId}/validate`, { method: 'POST' });
  },
  /** Assegna numero e data ed emette la fattura a partire dalla bozza. */
  async promoteDraft(companyId, draftId, body) {
    return apiCall(`/api/companies/${companyId}/invoices/drafts/${draftId}/promote`, { method: 'POST', body });
  },
  /** Anteprima del numero che la numerazione automatica assegnerebbe adesso. Non riserva nulla. */
  async nextNumber(companyId, { draftId, tipoDocumento, formatoTrasmissione, invoiceDate } = {}) {
    const params = new URLSearchParams();
    if (draftId) params.set('draftId', draftId);
    if (tipoDocumento) params.set('tipoDocumento', tipoDocumento);
    if (formatoTrasmissione) params.set('formatoTrasmissione', formatoTrasmissione);
    if (invoiceDate) params.set('invoiceDate', invoiceDate);
    const qs = params.toString();
    return apiCall(`/api/companies/${companyId}/invoices/next-number${qs ? `?${qs}` : ''}`);
  },
};

// ── Company payment methods ──────────────────────────────────────────

export const paymentMethodsApi = {
  async list(companyId, { includeInactive = false } = {}) {
    const qs = includeInactive ? '?includeInactive=true' : '';
    return apiCall(`/api/companies/${companyId}/payment-methods${qs}`);
  },
  async get(companyId, id) {
    return apiCall(`/api/companies/${companyId}/payment-methods/${id}`);
  },
  async create(companyId, data) {
    return apiCall(`/api/companies/${companyId}/payment-methods`, { method: 'POST', body: data });
  },
  async update(companyId, id, data) {
    return apiCall(`/api/companies/${companyId}/payment-methods/${id}`, { method: 'PUT', body: data });
  },
  async remove(companyId, id) {
    return apiCall(`/api/companies/${companyId}/payment-methods/${id}`, { method: 'DELETE' });
  },
};

// ── Invoice tranches (payment installments) ──────────────────────────

export const companyTranchesApi = {
  async list(companyId, invoiceId) {
    return apiCall(`/api/companies/${companyId}/invoices/${invoiceId}/tranches`);
  },
  async create(companyId, invoiceId, data) {
    return apiCall(`/api/companies/${companyId}/invoices/${invoiceId}/tranches`, { method: 'POST', body: data });
  },
  async update(companyId, invoiceId, trancheId, data) {
    return apiCall(`/api/companies/${companyId}/invoices/${invoiceId}/tranches/${trancheId}`, { method: 'PUT', body: data });
  },
  async remove(companyId, invoiceId, trancheId) {
    return apiCall(`/api/companies/${companyId}/invoices/${invoiceId}/tranches/${trancheId}`, { method: 'DELETE' });
  },
  async importFromXml(companyId, invoiceId) {
    return apiCall(`/api/companies/${companyId}/invoices/${invoiceId}/tranches/import-from-xml`, { method: 'POST' });
  },
};

// ── Scadenzario (scadenze di pagamento di tutte le fatture) ──────────

export const scadenzeApi = {
  /**
   * Scadenze di pagamento dell'azienda, su tutte le fatture insieme.
   *
   * `direction` distingue i due lati opposti — `incassi` sono le rate delle
   * fatture emesse (soldi che devono arrivare), `pagamenti` quelle delle
   * fatture ricevute (soldi che devono uscire) — e `status` sceglie il gruppo
   * mostrato: `aperte` (tutto ciò che non è ancora saldato), `scadute`,
   * `inScadenza`, `programmate`, `saldate`, `tutte`.
   *
   * I totali coprono sempre tutte le rate che corrispondono ai filtri, in
   * entrambe le modalità: `limit` taglia solo `items`, e `truncated` dice che
   * si è fermato l'elenco, non la somma. Quello che cambia con `view` è
   * `status`: con `elenco` (predefinito) i totali lo ignorano, così accanto
   * all'elenco c'è tutta la griglia dei bucket e la cifra scaduta resta
   * leggibile anche mentre l'elenco mostra le rate già saldate; con `totali`
   * non c'è nessun elenco accanto a cui leggerli e quindi coprono esattamente
   * il gruppo richiesto. La seconda è la modalità da usare per una cifra
   * mostrata da sola, senza righe sotto.
   *
   * Le rate delle note di credito (TD04/TD08) si sottraggono dai totali: gli
   * importi della riga restano positivi come sono memorizzati ed è `segno` (-1)
   * a dire da che parte vanno, mentre `notaDiCredito` dice di mostrarle come un
   * rimborso e non come un importo negativo senza spiegazione.
   *
   * @param {string} companyId
   * @param {{direction?: string, status?: string, from?: string, to?: string,
   *          counterparty?: string, dueSoonDays?: number, limit?: number,
   *          view?: 'elenco'|'totali'}} [filters]
   */
  async list(companyId, {
    direction, status, from, to, counterparty, dueSoonDays, limit, view,
  } = {}) {
    const params = new URLSearchParams();
    if (direction) params.set('direction', direction);
    if (status) params.set('status', status);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (counterparty) params.set('counterparty', counterparty);
    if (dueSoonDays != null) params.set('dueSoonDays', String(dueSoonDays));
    if (limit) params.set('limit', String(limit));
    if (view) params.set('view', view);
    return apiCall(`/api/companies/${companyId}/scadenze?${params}`);
  },
};

// ── Bilancio depositato (istanza XBRL) ────────────────────────────────

export const bilanciApi = {
  /** I bilanci in vigore dell'azienda, dall'esercizio più recente. */
  async list(companyId) {
    return apiCall(`/api/companies/${companyId}/bilanci`);
  },
  /**
   * Carica un bilancio: `{ contentBase64, filename, replace?, dryRun? }`. Con
   * `dryRun` il server legge e controlla il file, dice che cosa farebbe
   * (`preview.action`: create, unchanged, restore, replace) e non salva
   * niente. Senza: `outcome` created (201), unchanged o restored (200).
   */
  async upload(companyId, body) {
    return apiCall(`/api/companies/${companyId}/bilanci`, { method: 'POST', body });
  },
  /** Il bilancio con stato patrimoniale, conto economico, dati generali e testi. */
  async get(companyId, id) {
    return apiCall(`/api/companies/${companyId}/bilanci/${id}`);
  },
  /** Gli indici, con la base di ogni valore; `aliquotaIva` per i giorni di incasso e pagamento. */
  async analisi(companyId, id, { aliquotaIva } = {}) {
    const params = new URLSearchParams();
    if (aliquotaIva) params.set('aliquotaIva', aliquotaIva);
    const query = params.toString();
    return apiCall(`/api/companies/${companyId}/bilanci/${id}/analisi${query ? `?${query}` : ''}`);
  },
  /** Il riscontro tra il bilancio e le fatture in sdi dello stesso esercizio. */
  async riscontroFatture(companyId, id) {
    return apiCall(`/api/companies/${companyId}/bilanci/${id}/riscontro-fatture`);
  },
  /**
   * Scarica il file XBRL originale. Se la copia archiviata manca o è alterata
   * il server risponde 409 `bilancio_file_integrity` con `fileStatus`: si
   * ripristina ricaricando lo stesso file.
   * @returns {Promise<{blob: Blob, filename: string}>}
   */
  async downloadFile(companyId, id) {
    return downloadAttachment(`${API_BASE}/api/companies/${companyId}/bilanci/${id}/file`, 'bilancio.xbrl');
  },
  /** Elimina un bilancio caricato per errore; risponde con la data da cui potrà essere cancellato del tutto. */
  async remove(companyId, id) {
    return apiCall(`/api/companies/${companyId}/bilanci/${id}`, { method: 'DELETE' });
  },
};

// ── Modello ISA (#40) ─────────────────────────────────────────────────

export const isaApi = {
  /**
   * L'elenco dei modelli ISA di un periodo d'imposta (senza i campi) e, con
   * `ateco`, il modello in cui ricade quel codice per la tipologia di reddito.
   */
  async modelli(companyId, { anno, tipologia, ateco } = {}) {
    const params = new URLSearchParams();
    if (anno) params.set('anno', String(anno));
    if (tipologia) params.set('tipologia', tipologia);
    if (ateco) params.set('ateco', ateco);
    const query = params.toString();
    return apiCall(`/api/companies/${companyId}/isa/modelli${query ? `?${query}` : ''}`);
  },
  /** Un modello con tutti i suoi campi: righi, unità e stato di ogni campo. */
  async modello(companyId, codiceIsa, { anno, tipologia } = {}) {
    const params = new URLSearchParams();
    if (anno) params.set('anno', String(anno));
    if (tipologia) params.set('tipologia', tipologia);
    const query = params.toString();
    return apiCall(`/api/companies/${companyId}/isa/modelli/${encodeURIComponent(codiceIsa)}${query ? `?${query}` : ''}`);
  },
  /**
   * I valori che sdi ricava per un periodo, senza salvare niente. Senza
   * `codiceIsa` il modello si ricava dal codice ATECO del bilancio.
   */
  async calcolo(companyId, { periodo, bilancioId, codiceIsa, tipologia } = {}) {
    const params = new URLSearchParams({ periodo: String(periodo) });
    if (bilancioId) params.set('bilancioId', bilancioId);
    if (codiceIsa) params.set('codiceIsa', codiceIsa);
    if (tipologia) params.set('tipologia', tipologia);
    return apiCall(`/api/companies/${companyId}/isa/calcolo?${params}`);
  },
  /** Le schede ISA dell'azienda, dal periodo più recente. */
  async list(companyId) {
    return apiCall(`/api/companies/${companyId}/isa`);
  },
  /** Crea la scheda di un periodo: `{ periodoImposta, codiceIsa, tipologiaReddito, bilancioId?, codiceAttivita?, … }`. */
  async create(companyId, body) {
    return apiCall(`/api/companies/${companyId}/isa`, { method: 'POST', body });
  },
  /** Aggiorna i campi inviati; quelli assenti restano come sono. */
  async update(companyId, id, body) {
    return apiCall(`/api/companies/${companyId}/isa/${id}`, { method: 'PUT', body });
  },
  async remove(companyId, id) {
    return apiCall(`/api/companies/${companyId}/isa/${id}`, { method: 'DELETE' });
  },
  /** Il confronto rigo per rigo tra valori calcolati e dichiarati. */
  async confronto(companyId, id) {
    return apiCall(`/api/companies/${companyId}/isa/${id}/confronto`);
  },
  /**
   * Il confronto in CSV (UTF-8 con BOM, `;`, virgola decimale).
   * @returns {Promise<{blob: Blob, filename: string}>}
   */
  async downloadCsv(companyId, id) {
    return downloadAttachment(`${API_BASE}/api/companies/${companyId}/isa/${id}/confronto?format=csv`, 'confronto-isa.csv');
  },
};

// ── Autofatture (TD17/TD18 — flow 1: integrazione acquisti estero) ────

export const companyAutofattureApi = {
  async create(companyId, data) {
    return apiCall(`/api/companies/${companyId}/autofatture`, { method: 'POST', body: data });
  },
  /**
   * Crea una bozza di autofattura, senza numero né data. Non è modificabile:
   * si elimina con companyInvoicesApi.deleteDraft e si ricrea.
   */
  async createDraft(companyId, data) {
    return apiCall(`/api/companies/${companyId}/autofatture/drafts`, { method: 'POST', body: data });
  },
};

// ── Anagrafica import (Aruba .xls / .xlsx / .csv) ─────────────────────

export const anagraficaImportApi = {
  /**
   * @param {string} companyId
   * @param {{kind: 'cliente'|'fornitore', filename: string, contentBase64: string}} input
   */
  async importFile(companyId, input) {
    return apiCall(`/api/companies/${companyId}/customers/import`, {
      method: 'POST',
      body: input,
    });
  },
};

// ── Bulk-import historical FatturaPA XMLs ─────────────────────────────

export const companyImportApi = {
  /**
   * @param {string} companyId
   * @param {{filename: string, content: string}[]} files Array of XML files
   *   already read as text on the client.
   * @returns {Promise<{summary: {total,imported,skipped,failed}, results: any[], message: string}>}
   */
  async importBatch(companyId, files, { refreshMetadata = false } = {}) {
    return apiCall(`/api/companies/${companyId}/invoices/import`, {
      method: 'POST',
      body: { files, refreshMetadata },
    });
  },
};

// ── Admin: dev wipe-all-invoices ──────────────────────────────────────

export const adminInvoicesApi = {
  async wipeAll() {
    // The confirmation token is the phrase the admin types into the modal, so
    // it has to be the same string on both sides: diagnosticsAdmin.wipeAllConfirmPhrase
    // in the catalog, `confirm=` here, and the value src/api/admin.js compares
    // against. Change one without the others and the confirmation becomes
    // impossible to satisfy.
    return apiCall('/api/admin/invoices/wipe-all?confirm=CANCELLA_TUTTE_LE_FATTURE', {
      method: 'DELETE',
    });
  },
};

// ── User Roles ────────────────────────────────────────

export const userRolesApi = {
  async getMyRoles() {
    return apiCall('/api/user-roles/me');
  },
  async getAvailableRoles() {
    return apiCall('/api/user-roles/available');
  },
  async getAllUsers() {
    return apiCall('/api/user-roles/users');
  },
  async assignRole(userId, role) {
    return apiCall(`/api/user-roles/users/${userId}/roles`, {
      method: 'POST',
      body: { role },
    });
  },
  async removeRole(userId, role) {
    return apiCall(`/api/user-roles/users/${userId}/roles/${role}`, {
      method: 'DELETE',
    });
  },
  async setRoles(userId, roles) {
    return apiCall(`/api/user-roles/users/${userId}/roles`, {
      method: 'PUT',
      body: { roles },
    });
  },
};

// ── Scoped API keys ───────────────────────────────────
//
// Session-administrator only on the server: the global API key and the scoped
// keys themselves are refused. `create` is the one call whose answer carries a
// secret — `key`, shown once — so callers must not keep it beyond the screen.

export const apiKeysApi = {
  async permissions(companyId) {
    return apiCall(`/api/companies/${companyId}/api-keys/permissions`);
  },
  async list(companyId) {
    return apiCall(`/api/companies/${companyId}/api-keys`);
  },
  async create(companyId, data) {
    return apiCall(`/api/companies/${companyId}/api-keys`, { method: 'POST', body: data });
  },
  async revoke(companyId, keyId) {
    return apiCall(`/api/companies/${companyId}/api-keys/${keyId}/revoke`, { method: 'POST' });
  },
};
